import { Mail } from "lucide-react";
import React, { useMemo } from "react";
import ColouredBar from "./ColouredBar";
import {
  CHANNEL_ICONS,
  clampForBar,
  DASHBOARD_BAR_COLOR,
  formatPct,
} from "@/shared/utils/dashboardConfig";
import { useGetChannelEffectiveness } from "../../hooks/useGetDashboardApi";

const ChannelEffectiveness = ({ days = 30 }) => {
  const { data: channelEffectivenessData, isPending } =
    useGetChannelEffectiveness(days);

  const rows = useMemo(() => {
    const list = channelEffectivenessData?.channels;
    if (!Array.isArray(list)) return [];
    return list.map((item, index) => {
      const key = item.channel || item.label || `channel-${index}`;
      const Icon =
        CHANNEL_ICONS[String(item.channel || "").toLowerCase()] || Mail;
      return {
        key,
        label: item.label || item.channel || "—",
        icon: <Icon size={16} strokeWidth={1.5} className="text-secondary" />,
        engagement: Number(item.engagement_rate) || 0,
        conversion: Number(item.meeting_conversion) || 0,
      };
    });
  }, [channelEffectivenessData]);

  return (
    <div className="flex flex-col gap-1">
      {/* header row */}
      <div className="grid grid-cols-[1fr_2fr_2fr] border-b border-gray-200 dark:border-gray-800 pb-2 px-1">
        <p className="text-gray-700 dark:text-gray-300 text-[0.71rem] font-semibold">Channel</p>
        <p className="text-gray-700 dark:text-gray-300 text-[0.71rem] font-semibold">
          Engagement Rate
        </p>
        <p className="text-gray-700 dark:text-gray-300 text-[0.71rem] font-semibold">
          Meeting Conversion
        </p>
      </div>

      {isPending ? (
        <div className="flex flex-col gap-2 py-1 px-1">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_2fr_2fr] items-center gap-2 py-2 animate-pulse"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gray-200 dark:bg-gray-800" />
                <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-800" />
              </div>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-800 mr-4" />
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-800 mr-4" />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-[0.8rem] text-gray-500 dark:text-gray-400 py-6 text-center">
          No channel effectiveness data yet.
        </p>
      ) : (
        rows.map((row) => {
          const engBar = clampForBar(row.engagement);
          const convBar = clampForBar(row.conversion);
          return (
            <div
              key={row.key}
              className="grid grid-cols-[1fr_2fr_2fr] items-center py-2 px-1 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-800/80 border border-gray-100 dark:border-gray-800">
                  {row.icon}
                </div>
                <p className="text-gray-800 dark:text-gray-100 text-[0.81rem] font-medium">
                  {row.label}
                </p>
              </div>

              <div className="flex items-center gap-1.5 pr-4">
                <ColouredBar value={engBar} />
                <p
                  className="text-[0.8rem] font-semibold min-w-[2.5rem] text-right shrink-0 tabular-nums"
                  style={{ color: DASHBOARD_BAR_COLOR(engBar) }}
                >
                  {formatPct(row.engagement)}
                </p>
              </div>

              <div className="flex items-center gap-1.5 pr-4">
                <ColouredBar value={convBar} />
                <p
                  className="text-[0.8rem] font-semibold min-w-[2.5rem] text-right shrink-0 tabular-nums"
                  style={{ color: DASHBOARD_BAR_COLOR(convBar) }}
                >
                  {formatPct(row.conversion)}
                </p>
              </div>
            </div>
          );
        })
      )}

      {/* legend */}
      <div className="flex items-center justify-center gap-5 pt-2 mt-1">
        {[
          { color: "#16A34A", label: "Good" },
          { color: "#F97316", label: "Average" },
          { color: "#EF4444", label: "Low" },
        ].map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full inline-block"
              style={{ backgroundColor: color }}
            />
            <span className="text-[0.65rem] text-gray-600 dark:text-gray-400">{label}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default ChannelEffectiveness;
